import { useMemo } from "react";
import { Text, View, ActivityIndicator } from "react-native";
import { useUserEvents } from "@/features/events/api/event-participants";
import { Tag } from "@/shared/components/ui/tag";
import type { EventParticipantDetails } from "@lezz/shared";

const TOP_TAGS_COUNT = 7;

function computeTopTags(events: EventParticipantDetails[]): string[] {
  const freq: Record<string, number> = {};

  for (const event of events) {
    const tags = [
      ...((event.eventEventTags as string[] | null) ?? []),
      ...((event.eventVenueTags as string[] | null) ?? []),
      ...((event.eventMusicTags as string[] | null) ?? []),
    ];
    for (const tag of tags) {
      freq[tag] = (freq[tag] ?? 0) + 1;
    }
  }

  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_TAGS_COUNT)
    .map(([tag]) => tag);
}

interface FavoriteTagsProps {
  userId: string;
}

export function FavoriteTags({ userId }: FavoriteTagsProps) {
  const { data: events, isLoading } = useUserEvents(userId);

  const topTags = useMemo(() => computeTopTags(events ?? []), [events]);

  if (isLoading) {
    return (
      <ActivityIndicator
        size="small"
        className="text-brand"
        accessibilityLabel="Loading tags"
      />
    );
  }

  if (topTags.length === 0) return null;

  return (
    <View className="mb-6">
      <Text
        className="text-foreground font-display mb-3 text-xl font-semibold"
        accessibilityRole="header"
      >
        Tags favoritas
      </Text>
      <View className="flex-row flex-wrap gap-2">
        {topTags.map((tag) => (
          <Tag key={tag} label={tag} />
        ))}
      </View>
    </View>
  );
}
